import React from 'react'

import {
  DEFAULT,
  VARIANTS_UPDATED_EVENT,
  REGISTERED_PROVIDERS,
  getDNAProvider,
  createDNAProvider,
} from '@dna/core'

import { DNAProviderProps } from '@dna/react-types'

/**
 * Use settings from neareset DNAProvider.
 *
 * @name
 * DNAContext
 *
 * @see
 * https://dna.telstrahealth.com/components/dna-provider
 *
 * @example
 * React.useContext(DNAContext)
 */
export const DNAContext = React.createContext({
  variants: DEFAULT.INSTANCE.variants,
  setVariants: DEFAULT.INSTANCE.setVariants,
})

/**
 * The DNA Provider is a required component that enables sharing of
 * global settings throughout your application.
 *
 * @name
 * DNA Provider
 *
 * @see
 * https://dna.telstrahealth.com/components/dna-provider
 *
 * @example
 * <DNAProvider isRoot>
 *  <App />
 * <DNAProvider>
 */
export function DNAProvider(props: DNAProviderProps) {
  let {
    defaultVariants: { brand, theme = 'light', scale = 'medium' },
    isRoot,
    children,
  } = props
  let defaultVariants = { theme, scale, brand }
  let [variants, setVariants] = React.useState(DEFAULT.INSTANCE.variants)
  let [selector, setSelector] = React.useState('')

  React.useEffect(() => {
    if (typeof window === 'undefined')
      throw new Error(
        "window is 'undefined': DNA's Provider cannot run on SSR."
      )
    if (selector) return

    setSelector(
      isRoot
        ? DEFAULT.SELECTOR
        : `#dna-provider-${REGISTERED_PROVIDERS.size + 1}`
    )
  }, [REGISTERED_PROVIDERS.size])

  React.useEffect(() => {
    if (!selector || REGISTERED_PROVIDERS.has(selector)) return

    const element = document.querySelector(selector)
    const listener = () => setVariants(getDNAProvider(selector).variants)

    if (element != null)
      element.addEventListener(VARIANTS_UPDATED_EVENT, listener)

    createDNAProvider(defaultVariants, { selector })

    return () => {
      if (element != null)
        element.removeEventListener(VARIANTS_UPDATED_EVENT, listener)

      const instance = getDNAProvider(selector)
      instance && instance.remove()
    }
  }, [selector])

  return (
    <DNAContext.Provider
      value={{
        variants,
        // @ts-ignore
        setVariants: (newVariants) =>
          getDNAProvider(selector).setVariants(newVariants),
      }}
    >
      {selector === DEFAULT.SELECTOR ? (
        children
      ) : (
        <div id={selector.slice(1)}>{children}</div>
      )}
    </DNAContext.Provider>
  )
}
